import { DialogDisclosure, Heading } from "@ariakit/react";
import { DialogTrigger } from "@indietabletop/appkit";
import { useEffect, useRef, type ReactElement, type ReactNode } from "react";
import {
  DialogContent,
  DialogHeader,
  ScrollableDialog,
} from "../Dialog/Dialog";
import type { WarbandIssue } from "../engine/Warband";
import { conjunction, plural } from "../fmt";
import { useKnight } from "./KnightContext";
import { issues } from "./style.css";
import { useWarbandContext } from "./useWarbandContext";

function WarbandIssueMessage(props: { issue: WarbandIssue }) {
  const { issue } = props;

  switch (issue.type) {
    case "MAX_KNIGHTS_EXCEEDED": {
      return (
        <li className={issues.text}>
          A warband may contain at most{" "}
          {plural(issue.max, {
            one: "# knight",
            other: "# knights",
          })}
          , but currently there are {issue.current} present.
        </li>
      );
    }

    case "MAX_WORN_AND_USABLES_EXCEEDED": {
      return (
        <li className={issues.text}>
          A warband may contain at most{" "}
          {plural(issue.max, { one: "# item", other: "# items" })} from the worn
          or use items lists, but currently there are {issue.current} present.
        </li>
      );
    }

    case "MAX_CONSUMABLES_EXCEEDED": {
      return (
        <li className={issues.text}>
          A warband may contain at most{" "}
          {plural(issue.max, { one: "# item", other: "# items" })} from the
          consumable items list, but currently there are {issue.current}{" "}
          present.
        </li>
      );
    }

    case "INVALID_KNIGHTS": {
      return (
        <li className={issues.text}>
          A warband may not contain knights with invalid equipment, but{" "}
          {plural(issue.count, { one: "one is", other: "# are" })} present.
        </li>
      );
    }

    default: {
      return null;
    }
  }
}

function WarbandPeekMessage() {
  const { issues } = useWarbandContext();

  const peekIssue = issues.at(0);

  if (peekIssue && issues.length === 1) {
    switch (peekIssue.type) {
      case "MAX_KNIGHTS_EXCEEDED": {
        return "Too many knights for a Quick Battle.";
      }

      case "MAX_WORN_AND_USABLES_EXCEEDED": {
        return "Too many worn or use items for a Quick Battle.";
      }

      case "MAX_CONSUMABLES_EXCEEDED": {
        return "Too many consumables for a Quick Battle.";
      }

      case "INVALID_KNIGHTS": {
        return "Knights with invalid equipment present.";
      }

      default: {
        return null;
      }
    }
  }

  return `${plural(issues.length, { one: "# issue", other: "# issues" })} found for this warband.`;
}

function KnightPeekMessage() {
  const { issues } = useKnight();

  const peekIssue = issues.at(0);

  if (peekIssue && issues.length === 1) {
    switch (peekIssue.type) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      case "INVALID_EQUIPMENT_COMBINATION": {
        return "Invalid combination of equipment.";
      }

      default: {
        return null;
      }
    }
  }

  return `${plural(issues.length, { one: "# issue", other: "# issues" })} found for this warband.`;
}

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>(undefined);

  useEffect(() => {
    ref.current = value;
  });

  // eslint-disable-next-line react-hooks/refs
  return ref.current;
}

function WarbandIssuesDialog() {
  const { issues: warbandIssues } = useWarbandContext();

  return (
    <ScrollableDialog>
      <DialogHeader heading="Warband issues" />
      <DialogContent>
        <p className={issues.text}>
          Found{" "}
          {plural(warbandIssues.length, {
            one: "# issue",
            other: "# issues",
          })}{" "}
          when validating this warband against the <em>Quick Battle</em> rules.
        </p>
        <ul className={issues.list}>
          {warbandIssues.map((issue) => {
            return <WarbandIssueMessage key={issue.type} issue={issue} />;
          })}
        </ul>
      </DialogContent>
    </ScrollableDialog>
  );
}

function KnightIssuesDialog() {
  const { issues: knightIssues } = useKnight();

  return (
    <ScrollableDialog>
      <DialogHeader heading="Knight Issues" />
      <DialogContent>
        <p className={issues.text}>
          Found{" "}
          {plural(knightIssues.length, {
            one: "# issue",
            other: "# issues",
          })}{" "}
          when validating this knight.
        </p>
        <ul className={issues.list}>
          {knightIssues.map((issue) => {
            const message = `A knight maybe not equip ${conjunction(issue.equipment.map((item) => item.name))} together.`;

            return (
              <li className={issues.text} key={issue.type}>
                {message}
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </ScrollableDialog>
  );
}

function IssuesBox(props: {
  dialog: ReactElement;
  peek: ReactNode;
  isHidden: boolean;
}) {
  const { issues: warbandIssues } = useWarbandContext();
  const shouldAnimate = usePrevious(warbandIssues.length === 0);

  return (
    <div
      className={issues.container}
      data-hidden={props.isHidden}
      data-animate={shouldAnimate}
    >
      <DialogTrigger>
        {props.dialog}

        <DialogDisclosure className={issues.disclosure}>
          <Heading className={issues.heading}>Beware</Heading>
          <div className={issues.peek}>{props.peek}</div>
        </DialogDisclosure>
      </DialogTrigger>
    </div>
  );
}

export function WarbandIssues() {
  const { issues } = useWarbandContext();

  return (
    <IssuesBox
      isHidden={issues.length === 0}
      dialog={<WarbandIssuesDialog />}
      peek={<WarbandPeekMessage />}
    />
  );
}

export function KnightIssues() {
  const { issues } = useKnight();

  return (
    <IssuesBox
      isHidden={issues.length === 0}
      dialog={<KnightIssuesDialog />}
      peek={<KnightPeekMessage />}
    />
  );
}
